import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil, timeout, debounceTime, switchMap, filter, tap, Observable, of, catchError } from 'rxjs';
import { environment } from '../../../environment/environment';
import {
  DUNA_CATERING_PREFERENCE_OPTIONS,
  normalizeCateringPreferenceSelection,
} from '../../../constants/duna-catering-preference-options';
import {
  mergeCateringPreferenceSummary,
  preferenceComboSummaryKey as sharedPreferenceComboSummaryKey,
  preferenceComboSummaryLabel as sharedPreferenceComboSummaryLabel,
  preferenceSummaryLabel as sharedPreferenceSummaryLabel,
} from '../../../constants/catering-summary-labels';
import { CateringGroupDTO } from '../../../dto/catering/catering-group.dto';
import {
  countParticipantsForCatering,
  shouldCountParticipantForCatering,
} from '../../../constants/catering-participant-count';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** mat-select value for primary / standard event room */
const CATERING_ROOM_STANDARD = '';

export interface ParticipantItem {
  user_id: number;
  user_uuid?: string | null;
  name: string;
  email?: string | null;
  role?: string | null;
  status?: string | null;
  catering_preferences: string[];
}

export interface CateringExtraOptionDTO {
  id: number;
  organizations_id?: number;
  label: string;
  is_active: boolean;
  auto_include?: boolean;
  sort_order: number;
}

export interface EventCateringExtraLineDTO {
  line_id?: string;
  extra_option_id: number;
  quantity: number;
  label: string;
  is_active?: boolean;
  comment?: string | null;
}

export interface CateringRoomOptionDTO {
  guid: string;
  room_name: string;
}

export interface DunaCateringPreferencesResponse {
  event_id: number;
  event_guid?: string | null;
  event_name?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  location?: string | null;
  catering?:
    | 'self_catering'
    | 'catering_included'
    | 'catering_not_included'
    | string
    | null;
  items: ParticipantItem[];
  summary: Record<string, number>;
  extra_options?: CateringExtraOptionDTO[];
  event_catering_extras?: EventCateringExtraLineDTO[];
  extras_summary?: EventCateringExtraLineDTO[];
  /** Extra-option IDs with an explicit quantity saved on the event (duna_events.data.catering_extras). */
  defined_catering_extra_option_ids?: number[];
  /** Optional comment for the whole catering order (duna_events.data.catering_order_comment). */
  catering_order_comment?: string | null;
  predefined_room?: CateringRoomOptionDTO | null;
  additional_rooms?: CateringRoomOptionDTO[];
  /** null = Standardlokale (primary room); otherwise GUID of an additional room. */
  catering_room_guid?: string | null;
}

export interface CateringWishesEventMeta {
  eventName?: string | null;
  guid?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  location?: string | null;
}

interface ResolvedContext {
  eventId: number;
  eventGuid: string | null;
  meta: CateringWishesEventMeta;
  catering: string | null;
}

interface EventParticipantDTO {
  user?: {
    id?: number;
    uuid?: string;
    first_name?: string;
    last_name?: string;
    user?: string;
  };
  role?: string;
  status?: string;
  data?: Record<string, any> | null;
}

@Component({
  selector: 'app-duna-catering-wishes',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTableModule,
    MatSortModule,
    MatTooltipModule,
    MatDividerModule,
  ],
  templateUrl: './duna-catering-wishes.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-catering-wishes.component.scss',
})
export class DunaCateringWishesComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() eventId: number | null = null;
  @Input() eventMeta: CateringWishesEventMeta | null = null;
  @Input() showCloseButton = false;
  @Output() closeRequested = new EventEmitter<void>();

  displayedColumns: string[] = [
    'name',
    'email',
    'role',
    'status',
    'preferences',
  ];
  dataSource = new MatTableDataSource<ParticipantItem>([]);

  searchTerm = '';
  selectedPreferenceFilter = '';
  loading = false;
  errorMessage: string | null = null;
  groupErrorMessage: string | null = null;
  groupFeatureAvailable = true;
  loadingGroups = false;
  savingGroups = false;
  groupsSaveStatus: 'idle' | 'saving' | 'saved' | 'error' = 'idle';
  availableGroups: CateringGroupDTO[] = [];
  selectedGroupIds: number[] = [];
  readonly preferenceOptions = DUNA_CATERING_PREFERENCE_OPTIONS;
  private readonly roleLabels: Record<string, string> = {
    attendee: 'Deltager',
    instructor: 'Instruktør',
    assistant: 'Assistent',
    empty: '-',
  };
  private readonly statusLabels: Record<string, string> = {
    waitlist: 'Venteliste',
    pending: 'Tilmeldt',
    shownup: 'Mødt op',
    requires_attention: 'Kræver opmærksomhed',
    completed: 'Gennemført',
    not_appear: 'Udeblevet',
    cancellation: 'Afbud',
    not_participant: 'Ikke deltager',
    empty: '-',
  };
  private response: DunaCateringPreferencesResponse | null = null;
  private context: ResolvedContext | null = null;
  private destroy$ = new Subject<void>();
  private editedPreferences: Record<string, string[]> = {};
  private savingParticipants = new Set<string>();
  private initialGroupIds: number[] = [];
  private suppressGroupsAutoSave = false;
  private groupsAutoSave$ = new Subject<void>();
  private groupsSavedStatusTimer: ReturnType<typeof setTimeout> | null = null;

  /** Kladder til event-niveau forplejningskatalog (gemmes i duna_events.data.catering_extras). */
  eventExtraLinesDraft: Array<{
    line_id: string;
    extra_option_id: number | null;
    /** Justering op/ned fra antal tilmeldte deltagere (null = ingen justering). */
    quantity_adjustment: number | null;
    locked?: boolean;
    comment?: string;
  }> = [];
  cateringOrderCommentDraft = '';
  cateringRoomGuidDraft = CATERING_ROOM_STANDARD;
  savingEventExtras = false;
  eventExtrasError: string | null = null;
  eventExtrasSaveStatus: 'idle' | 'saving' | 'saved' | 'error' = 'idle';
  /** Ekstra-linjer hvor kommentarfeltet er manuelt udvidet. */
  private expandedExtraCommentKeys = new Set<string>();
  private eventExtrasAutoSave$ = new Subject<void>();
  private eventExtrasSavedStatusTimer: ReturnType<typeof setTimeout> | null = null;

  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private http: HttpClient,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.configureFilterPredicate();
    this.setupEventExtrasAutoSave();
    this.setupGroupsAutoSave();
    this.load();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'preferences':
          return (item.catering_preferences || []).join(', ');
        default:
          return (item as any)[property] || '';
      }
    };
  }

  ngOnDestroy(): void {
    this.clearEventExtrasSavedStatusTimer();
    this.clearGroupsSavedStatusTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get summaryKeys(): string[] {
    return this.sortSummaryComboKeys(
      Object.keys(this.mergedPreferenceSummary)
    );
  }

  private get mergedPreferenceSummary(): Record<string, number> {
    return mergeCateringPreferenceSummary(this.response?.summary);
  }

  get eventExtrasSummaryLines(): EventCateringExtraLineDTO[] {
    const summary = this.response?.extras_summary;
    if (summary && summary.length > 0) {
      return summary;
    }
    const extras = this.response?.event_catering_extras;
    if (extras && extras.length > 0) {
      return extras;
    }
    return [];
  }

  /** Opsummering til visning — følger altid kladden (samme tal som i editoren). */
  get eventExtrasDisplayLines(): EventCateringExtraLineDTO[] {
    return this.eventExtraLinesDraft
      .filter((row) => row.extra_option_id != null && row.extra_option_id > 0)
      .map((row) => {
        const id = row.extra_option_id as number;
        const opt = (this.response?.extra_options || []).find(
          (o) => o.id === id
        );
        return {
          extra_option_id: id,
          quantity: this.effectiveExtraQuantity(row),
          label: opt?.label ?? `#${id}`,
          is_active: opt?.is_active ?? true,
          comment: (row.comment ?? '').trim() || null,
        };
      });
  }

  /** Skjul event-ekstra når catering er eksplicit fravalgt. */
  get canShowEventCateringExtras(): boolean {
    return (
      (this.response?.catering ?? this.context?.catering) !==
      'catering_not_included'
    );
  }

  get activeExtraOptions(): CateringExtraOptionDTO[] {
    return (this.response?.extra_options || []).filter((o) => o.is_active);
  }

  get hasCateringOrderSettingsChanges(): boolean {
    const orderCommentServer = (
      this.response?.catering_order_comment ?? ''
    ).trim();
    const orderCommentDraft = this.cateringOrderCommentDraft.trim();
    const roomServer =
      (this.response?.catering_room_guid ?? '').trim() ||
      CATERING_ROOM_STANDARD;
    const roomDraft =
      this.cateringRoomGuidDraft.trim() || CATERING_ROOM_STANDARD;
    return orderCommentDraft !== orderCommentServer || roomDraft !== roomServer;
  }

  get standardCateringRoomLabel(): string {
    return 'Standardlokale';
  }

  get allEventRooms(): CateringRoomOptionDTO[] {
    const rooms: CateringRoomOptionDTO[] = [];
    const seen = new Set<string>();
    const primary = this.response?.predefined_room;
    if (primary?.guid) {
      const guid = primary.guid.trim();
      if (guid) {
        rooms.push(primary);
        seen.add(guid.toLowerCase());
      }
    }
    for (const room of this.response?.additional_rooms ?? []) {
      const guid = room.guid?.trim();
      if (!guid || seen.has(guid.toLowerCase())) {
        continue;
      }
      seen.add(guid.toLowerCase());
      rooms.push(room);
    }
    return rooms;
  }

  get cateringRoomOptions(): Array<{ value: string; label: string }> {
    const options: Array<{ value: string; label: string }> = [
      { value: CATERING_ROOM_STANDARD, label: this.standardCateringRoomLabel },
    ];
    for (const room of this.allEventRooms) {
      const guid = room.guid?.trim();
      if (!guid) {
        continue;
      }
      const name = (room.room_name || '').trim() || guid;
      options.push({ value: guid, label: name });
    }
    return options;
  }

  compareCateringRoomValue = (
    a: string | null | undefined,
    b: string | null | undefined
  ): boolean => (a ?? CATERING_ROOM_STANDARD) === (b ?? CATERING_ROOM_STANDARD);

  get selectedCateringRoomLabel(): string {
    const draft = this.cateringRoomGuidDraft || CATERING_ROOM_STANDARD;
    const selected = this.cateringRoomOptions.find(
      (opt) => opt.value === draft
    );
    return selected?.label ?? this.standardCateringRoomLabel;
  }

  get hasEventExtraDraftChanges(): boolean {
    const server = this.normalizeAbsoluteExtraLinesForCompare(
      this.response?.event_catering_extras || []
    );
    const draft = this.normalizeDraftExtraLinesForCompare(
      this.eventExtraLinesDraft
    );
    return JSON.stringify(server) !== JSON.stringify(draft);
  }

  get allPreferences(): string[] {
    const prefs = new Set<string>();
    (this.response?.items || []).forEach((item) => {
      (item.catering_preferences || []).forEach((pref) => prefs.add(pref));
    });
    return Array.from(prefs).sort((a, b) => a.localeCompare(b));
  }

  /** Participants that count toward catering (excludes afbud, venteliste, ikke deltager). */
  get cateringEligibleParticipantCount(): number {
    return countParticipantsForCatering(this.response?.items);
  }

  get totalParticipants(): number {
    return this.cateringEligibleParticipantCount;
  }

  /** Base count for catalog quantities (same rule as preference summary). */
  get pendingParticipantCount(): number {
    return this.cateringEligibleParticipantCount;
  }

  effectiveExtraQuantity(row: {
    quantity_adjustment?: number | null;
  }): number {
    const adjustment = this.coerceExtraQuantityAdjustment(row.quantity_adjustment);
    return this.pendingParticipantCount + adjustment;
  }

  private coerceExtraQuantityAdjustment(
    value: number | null | undefined
  ): number {
    if (value == null || Number.isNaN(Number(value))) {
      return 0;
    }
    return Number(value);
  }

  private absoluteQuantityToAdjustment(
    absoluteQuantity: number
  ): number | null {
    const adjustment = absoluteQuantity - this.pendingParticipantCount;
    return adjustment === 0 ? null : adjustment;
  }

  get filteredCount(): number {
    return countParticipantsForCatering(this.dataSource.filteredData);
  }

  get hasData(): boolean {
    return (this.response?.items?.length ?? 0) > 0;
  }

  get headerEventName(): string {
    return (
      this.context?.meta?.eventName ||
      this.context?.meta?.guid ||
      (this.context ? `Event #${this.context.eventId}` : 'Event')
    );
  }

  get headerStartDate(): string | null {
    return this.context?.meta?.startDate || null;
  }

  get headerEndDate(): string | null {
    return this.context?.meta?.endDate || null;
  }

  get headerDateTimeRange(): string {
    return this.formatEventDateTimeRange(
      this.headerStartDate,
      this.headerEndDate
    );
  }

  get headerLocation(): string | null {
    return this.context?.meta?.location || null;
  }

  summaryCount(key: string): number {
    return this.mergedPreferenceSummary[key] ?? 0;
  }

  preferenceSummaryLabel(key: string): string {
    return sharedPreferenceSummaryLabel(key);
  }

  /** Visningsnavn for opsummering: én linje pr. person (fx «Glutenfri & Halal»). */
  preferenceComboSummaryLabel(comboKey: string): string {
    return sharedPreferenceComboSummaryLabel(comboKey);
  }

  roleLabel(role: string | null | undefined): string {
    return this.roleLabels[role || 'empty'] || role || '-';
  }

  statusLabel(status: string | null | undefined): string {
    return this.statusLabels[status || 'empty'] || status || '-';
  }

  groupLabel(item: CateringGroupDTO): string {
    const count = item.member_count ?? 0;
    return count > 0 ? `${item.name} (${count} ansvarlige)` : item.name;
  }

  hasGroupChanges(): boolean {
    const current = [...this.selectedGroupIds].sort((a, b) => a - b);
    const initial = [...this.initialGroupIds].sort((a, b) => a - b);
    if (current.length !== initial.length) return true;
    return current.some((value, index) => value !== initial[index]);
  }

  onCateringGroupsChange(): void {
    if (this.loadingGroups || this.savingGroups || this.suppressGroupsAutoSave) {
      return;
    }
    this.groupErrorMessage = null;
    this.groupsSaveStatus = 'idle';
    this.queueGroupsAutoSave();
  }

  private queueGroupsAutoSave(): void {
    this.groupsAutoSave$.next();
  }

  private setupGroupsAutoSave(): void {
    this.groupsAutoSave$
      .pipe(
        debounceTime(400),
        filter(() => this.hasGroupChanges() && !this.loadingGroups),
        tap(() => {
          this.groupsSaveStatus = 'saving';
          this.cdr.detectChanges();
        }),
        switchMap(() => this.patchCateringGroups$()),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.groupsSaveStatus = 'saved';
          this.clearGroupsSavedStatusTimer();
          this.groupsSavedStatusTimer = setTimeout(() => {
            if (this.groupsSaveStatus === 'saved') {
              this.groupsSaveStatus = 'idle';
              this.cdr.detectChanges();
            }
          }, 2000);
          this.cdr.detectChanges();
        },
        error: () => {
          this.groupsSaveStatus = 'error';
          this.cdr.detectChanges();
        },
      });
  }

  private clearGroupsSavedStatusTimer(): void {
    if (this.groupsSavedStatusTimer != null) {
      clearTimeout(this.groupsSavedStatusTimer);
      this.groupsSavedStatusTimer = null;
    }
  }

  private patchCateringGroups$(): Observable<{ items: CateringGroupDTO[] }> {
    const url = this.cateringGroupsApiUrl();
    if (!url) {
      return of({ items: [] });
    }

    this.savingGroups = true;
    this.groupErrorMessage = null;
    return this.http
      .post<{ items: CateringGroupDTO[] }>(url, {
        catering_group_ids: this.selectedGroupIds,
      })
      .pipe(
        tap((response) => {
          const selected = (response?.items || [])
            .map((item) => item?.catering_group_id || 0)
            .filter((value) => value > 0)
            .sort((a, b) => a - b);
          this.initialGroupIds = selected;
          this.selectedGroupIds = [...selected];
          this.savingGroups = false;
        }),
        catchError((error) => {
          this.savingGroups = false;
          this.groupErrorMessage = this.formatApiError(
            error,
            'Kunne ikke gemme forplejningsgrupper.'
          );
          throw error;
        })
      );
  }

  private cateringGroupsApiUrl(): string | null {
    const eventId = this.context?.eventId;
    if (!eventId || eventId <= 0) {
      return null;
    }
    return `${environment.apiUrl}/events/catering/event-groups/${eventId}`;
  }

  load(): void {
    this.loading = true;
    this.errorMessage = null;
    const inputGuid = this.eventMeta?.guid || null;
    if (inputGuid && UUID_REGEX.test(inputGuid)) {
      this.loadFromGuid(inputGuid);
      return;
    }

    if (this.eventId && this.eventId > 0) {
      this.fetchPreferencesById(this.eventId, inputGuid);
      return;
    }

    const guid = this.extractGuidFromUrl();
    if (!guid) {
      this.loading = false;
      this.errorMessage = 'No valid event identifier found';
      this.cdr.detectChanges();
      return;
    }

    this.loadFromGuid(guid);
  }

  private loadFromGuid(guid: string): void {
    this.http
      .get(`${environment.apiUrl}/events/${guid}`, { responseType: 'text' })
      .pipe(timeout(10000), takeUntil(this.destroy$))
      .subscribe({
        next: (rawBody) => {
          const rawEvent = this.parseApiPayload<any>(rawBody);
          if (!rawEvent) {
            this.loading = false;
            this.errorMessage = 'API returnerede ugyldigt event-svar.';
            this.cdr.detectChanges();
            return;
          }
          const event = this.normalizeEventResponse(rawEvent);
          if (!event || !event.event_id) {
            this.loading = false;
            this.errorMessage = 'Kunne ikke hente event-data.';
            this.cdr.detectChanges();
            return;
          }

          const context: ResolvedContext = {
            eventId: event.event_id,
            eventGuid: event.guid || guid,
            catering: event.catering || null,
            meta: this.mergeMeta(
              {
                eventName: this.resolveEventName(event, guid),
                guid: event.guid || guid,
                startDate: event.start_date || null,
                endDate: event.end_date || null,
                location: this.resolveEventLocation(event),
              },
              this.eventMeta
            ),
          };

          if (context.catering === 'catering_not_included') {
            this.loading = false;
            this.errorMessage = 'Eventet har ikke forplejning inkluderet.';
            this.cdr.detectChanges();
            return;
          }

          this.context = context;
          this.fetchPreferencesById(
            context.eventId,
            context.eventGuid || guid,
            context.meta
          );
        },
        error: (error) => {
          this.loading = false;
          this.errorMessage =
            error?.error?.message ||
            error?.message ||
            'Kunne ikke hente event ud fra GUID.';
          this.cdr.detectChanges();
        },
      });
  }

  private fetchPreferencesById(
    eventId: number,
    fallbackGuid: string | null,
    prefilledMeta?: CateringWishesEventMeta | null
  ): void {
    this.http
      .get(`${environment.apiUrl}/events/catering/preferences/${eventId}`, {
        responseType: 'text',
      })
      .pipe(timeout(10000), takeUntil(this.destroy$))
      .subscribe({
        next: (rawBody) => {
          const rawParsed = this.parseApiPayload<unknown>(rawBody);
          const response = this.normalizeCateringPreferencesResponse(
            this.unwrapCateringPreferencesPayload(rawParsed)
          );
          if (
            !response ||
            !this.isUsableCateringPreferencesResponse(response)
          ) {
            this.logCateringPreferencesPayloadFailure(
              eventId,
              fallbackGuid,
              rawBody,
              rawParsed
            );
            this.loading = false;
            this.errorMessage =
              'API returnerede ugyldigt svar for forplejningsønsker.';
            this.cdr.detectChanges();
            return;
          }
          if ((response.catering ?? '') === 'catering_not_included') {
            this.loading = false;
            this.errorMessage = 'Eventet har ikke forplejning inkluderet.';
            this.cdr.detectChanges();
            return;
          }
          const context: ResolvedContext = {
            eventId: response.event_id || eventId,
            eventGuid:
              response.event_guid ||
              fallbackGuid ||
              this.eventMeta?.guid ||
              null,
            catering: response.catering || null,
            meta: this.mergeMeta(
              this.mergeMeta(
                {
                  eventName:
                    response.event_name ||
                    response.event_guid ||
                    `Event #${response.event_id || eventId}`,
                  guid: response.event_guid || fallbackGuid || null,
                  startDate: response.start_date || null,
                  endDate: response.end_date || null,
                  location: response.location || null,
                },
                this.eventMeta
              ),
              prefilledMeta || {}
            ),
          };
          this.context = context;
          this.response = response;
          this.enrichRoomsFromEventIfNeeded(context.eventGuid);
          this.dataSource.data = (response.items || []).map((item) => ({
            ...item,
            catering_preferences: this.normalizePreferences(
              item.catering_preferences || []
            ),
          }));
          this.initializeEditedPreferences();
          this.rebuildSummaryFromCurrentItems();
          this.hydrateEventExtrasDraftFromResponse(response);
          if (this.hasEventExtraDraftChanges) {
            this.queueEventExtrasAutoSave();
          }
          this.applyFilter();
          this.loading = false;
          this.refreshCateringGroups(context.eventGuid);
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.loading = false;
          console.error(
            '[duna-catering-wishes] catering/preferences HTTP error',
            {
              eventId,
              fallbackGuid,
              status: (error as { status?: number })?.status,
              error,
            }
          );
          this.errorMessage =
            error?.error?.message ||
            error?.message ||
            'Kunne ikke hente forplejningsønsker.';
          this.cdr.detectChanges();
        },
      });
  }

  addEventExtraRow(): void {
    this.eventExtraLinesDraft.push({
      line_id: this.newExtraLineId(),
      extra_option_id: null,
      quantity_adjustment: null,
      comment: '',
    });
    this.onEventExtraDraftChange();
  }

  removeEventExtraRow(index: number): void {
    if (this.eventExtraLinesDraft[index]?.locked) {
      return;
    }
    this.eventExtraLinesDraft.splice(index, 1);
    this.onEventExtraDraftChange();
  }

  onEventExtraDraftChange(): void {
    this.eventExtrasError = null;
    this.eventExtrasSaveStatus = 'idle';
    this.queueEventExtrasAutoSave();
  }

  private queueEventExtrasAutoSave(): void {
    this.eventExtrasAutoSave$.next();
  }

  private setupEventExtrasAutoSave(): void {
    this.eventExtrasAutoSave$
      .pipe(
        debounceTime(500),
        filter(() => this.hasEventExtraDraftChanges),
        tap(() => {
          this.eventExtrasSaveStatus = 'saving';
          this.cdr.detectChanges();
        }),
        switchMap(() => this.patchEventCateringExtras$()),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.eventExtrasSaveStatus = 'saved';
          this.clearEventExtrasSavedStatusTimer();
          this.eventExtrasSavedStatusTimer = setTimeout(() => {
            if (this.eventExtrasSaveStatus === 'saved') {
              this.eventExtrasSaveStatus = 'idle';
              this.cdr.detectChanges();
            }
          }, 2000);
          this.cdr.detectChanges();
        },
        error: () => {
          this.eventExtrasSaveStatus = 'error';
          this.cdr.detectChanges();
        },
      });
  }

  private clearEventExtrasSavedStatusTimer(): void {
    if (this.eventExtrasSavedStatusTimer != null) {
      clearTimeout(this.eventExtrasSavedStatusTimer);
      this.eventExtrasSavedStatusTimer = null;
    }
  }

  showExtraCommentField(
    row: { line_id: string; comment?: string },
    index: number
  ): boolean {
    return (
      !!(row.comment ?? '').trim() ||
      this.expandedExtraCommentKeys.has(this.extraCommentKey(row, index))
    );
  }

  expandExtraComment(row: { line_id: string }, index: number): void {
    this.expandedExtraCommentKeys.add(this.extraCommentKey(row, index));
  }

  collapseExtraComment(
    row: { line_id: string; comment?: string },
    index: number
  ): void {
    if ((row.comment ?? '').trim()) {
      return;
    }
    this.expandedExtraCommentKeys.delete(this.extraCommentKey(row, index));
  }

  onExtraCommentBlur(
    row: { line_id: string; comment?: string },
    index: number
  ): void {
    this.collapseExtraComment(row, index);
  }

  private extraCommentKey(row: { line_id: string }, index: number): string {
    return row.line_id || `idx:${index}`;
  }

  private resetExpandedExtraComments(): void {
    this.expandedExtraCommentKeys.clear();
  }

  saveEventCateringExtras(): void {
    if (!this.context?.eventGuid) {
      return;
    }
    if (!this.hasEventExtraDraftChanges && !this.hasCateringOrderSettingsChanges) {
      return;
    }
    this.eventExtrasSaveStatus = 'saving';
    this.eventExtrasError = null;
    this.patchEventCateringExtras$()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.eventExtrasSaveStatus = 'saved';
          this.clearEventExtrasSavedStatusTimer();
          this.eventExtrasSavedStatusTimer = setTimeout(() => {
            if (this.eventExtrasSaveStatus === 'saved') {
              this.eventExtrasSaveStatus = 'idle';
              this.cdr.detectChanges();
            }
          }, 2000);
          this.cdr.detectChanges();
        },
        error: () => {
          this.eventExtrasSaveStatus = 'error';
          this.cdr.detectChanges();
        },
      });
  }

  private buildEventCateringExtrasPayload(): {
    lines: Array<{
      line_id: string;
      extra_option_id: number;
      quantity: number;
      comment?: string;
    }>;
    order_comment: string;
    catering_room_guid: string | null;
  } {
    const lines = this.eventExtraLinesDraft
      .filter((row) => {
        if (row.extra_option_id == null || row.extra_option_id <= 0) {
          return false;
        }
        const effectiveQty = this.effectiveExtraQuantity(row);
        const comment = (row.comment ?? '').trim();
        return row.locked || effectiveQty > 0 || comment.length > 0;
      })
      .map((row) => {
        const entry: {
          line_id: string;
          extra_option_id: number;
          quantity: number;
          comment?: string;
        } = {
          line_id: row.line_id,
          extra_option_id: row.extra_option_id as number,
          quantity: this.effectiveExtraQuantity(row),
        };
        const comment = (row.comment ?? '').trim();
        if (comment) {
          entry.comment = comment;
        }
        return entry;
      });
    const roomGuid = this.cateringRoomGuidDraft.trim();
    return {
      lines,
      order_comment: this.cateringOrderCommentDraft.trim(),
      catering_room_guid: roomGuid || null,
    };
  }

  private patchEventCateringExtras$(): Observable<void> {
    const guid = this.context?.eventGuid;
    if (!guid) {
      return of(undefined);
    }
    const payload = this.buildEventCateringExtrasPayload();
    this.savingEventExtras = true;
    this.eventExtrasError = null;
    return this.http
      .patch<{
        catering_extras: EventCateringExtraLineDTO[];
        extras_summary: EventCateringExtraLineDTO[];
        catering_order_comment?: string | null;
        catering_room_guid?: string | null;
        additional_rooms?: CateringRoomOptionDTO[];
        predefined_room?: CateringRoomOptionDTO | null;
      }>(`${environment.apiUrl}/events/${guid}/catering-extras`, payload)
      .pipe(
        tap((rawRes) => {
          const res = this.unwrapCateringExtrasPatchResponse(rawRes);
          this.applyEventExtrasResponseSnapshot(payload, res);
          this.savingEventExtras = false;
        }),
        catchError((error) => {
          this.savingEventExtras = false;
          this.eventExtrasError = this.formatApiError(
            error,
            'Kunne ikke gemme forplejningskatalog.'
          );
          throw error;
        }),
        switchMap(() => of(undefined))
      );
  }

  private applyEventExtrasResponseSnapshot(
    payload: {
      lines: Array<{
        line_id: string;
        extra_option_id: number;
        quantity: number;
        comment?: string;
      }>;
      order_comment: string;
      catering_room_guid: string | null;
    },
    res: {
      catering_extras?: EventCateringExtraLineDTO[];
      extras_summary?: EventCateringExtraLineDTO[];
      catering_order_comment?: string | null;
      catering_room_guid?: string | null;
      additional_rooms?: CateringRoomOptionDTO[];
      predefined_room?: CateringRoomOptionDTO | null;
    } | null
  ): void {
    if (!this.response) {
      return;
    }
    const apiLines = res?.catering_extras ?? [];
    const payloadLines = this.buildResolvedExtrasFromPayload(payload.lines);
    this.response.event_catering_extras =
      apiLines.length >= payload.lines.length ? apiLines : payloadLines;
    this.response.extras_summary =
      res?.extras_summary && res.extras_summary.length >= payload.lines.length
        ? res.extras_summary
        : payloadLines;
    this.response.catering_order_comment =
      res?.catering_order_comment ?? payload.order_comment ?? '';
    this.response.catering_room_guid =
      res?.catering_room_guid ?? payload.catering_room_guid ?? null;
    if (res?.additional_rooms) {
      this.response.additional_rooms = this.normalizeAdditionalRooms(
        res.additional_rooms
      );
    }
    if (res?.predefined_room) {
      this.response.predefined_room = this.normalizePredefinedRoom(
        res.predefined_room
      );
    }
    const defined = new Set(this.response.defined_catering_extra_option_ids ?? []);
    payload.lines.forEach((l) => defined.add(l.extra_option_id));
    this.response.defined_catering_extra_option_ids = Array.from(defined);
    this.cateringOrderCommentDraft = this.response.catering_order_comment ?? '';
    const storedRoomGuid = this.response.catering_room_guid?.trim() ?? '';
    this.cateringRoomGuidDraft = storedRoomGuid || CATERING_ROOM_STANDARD;
  }

  private lockedAutoExtraLineId(extraOptionId: number): string {
    return `auto-${extraOptionId}`;
  }

  private isLockedAutoExtraSavedLine(
    line: { line_id?: string; extra_option_id: number },
    catalog: CateringExtraOptionDTO[]
  ): boolean {
    if (!this.isAutoIncludedExtraOption(line.extra_option_id, catalog)) {
      return false;
    }
    const lineId = (line.line_id ?? '').trim();
    return lineId === this.lockedAutoExtraLineId(line.extra_option_id);
  }

  private findLockedAutoExtraSavedLine(
    extraOptionId: number,
    savedLines: EventCateringExtraLineDTO[]
  ): EventCateringExtraLineDTO | undefined {
    const lockedLineId = this.lockedAutoExtraLineId(extraOptionId);
    const byLockedId = savedLines.find(
      (l) =>
        l.extra_option_id === extraOptionId &&
        (l.line_id ?? '').trim() === lockedLineId
    );
    if (byLockedId) {
      return byLockedId;
    }
    const matching = savedLines.filter(
      (l) => l.extra_option_id === extraOptionId
    );
    if (matching.length === 1) {
      const lineId = (matching[0].line_id ?? '').trim();
      if (lineId === '' || lineId === lockedLineId) {
        return matching[0];
      }
    }
    return undefined;
  }

  private hydrateEventExtrasDraftFromResponse(
    response: DunaCateringPreferencesResponse | null
  ): void {
    const catalog = response?.extra_options || [];
    const savedLines = response?.event_catering_extras || [];

    const draft: Array<{
      line_id: string;
      extra_option_id: number | null;
      quantity_adjustment: number | null;
      locked?: boolean;
      comment?: string;
    }> = [];

    savedLines.forEach((l) => {
      if (this.isLockedAutoExtraSavedLine(l, catalog)) {
        return;
      }
      draft.push({
        line_id: l.line_id || this.newExtraLineId(),
        extra_option_id: l.extra_option_id,
        quantity_adjustment: this.absoluteQuantityToAdjustment(l.quantity),
        locked: false,
        comment: (l.comment ?? '').trim(),
      });
    });

    catalog
      .filter((o) => o.is_active && o.auto_include)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id)
      .forEach((o) => {
        const lockedSaved = this.findLockedAutoExtraSavedLine(o.id, savedLines);
        const hasLockedSaved = lockedSaved != null;
        draft.push({
          line_id: this.lockedAutoExtraLineId(o.id),
          extra_option_id: o.id,
          quantity_adjustment: hasLockedSaved
            ? this.absoluteQuantityToAdjustment(lockedSaved.quantity ?? 0)
            : null,
          locked: true,
          comment: hasLockedSaved ? (lockedSaved.comment ?? '').trim() : '',
        });
      });

    if (draft.length === 0) {
      draft.push({
        line_id: this.newExtraLineId(),
        extra_option_id: null,
        quantity_adjustment: null,
        comment: '',
      });
    }
    this.eventExtraLinesDraft = draft;
    this.cateringOrderCommentDraft = response?.catering_order_comment ?? '';
    const storedRoomGuid = response?.catering_room_guid?.trim() ?? '';
    this.cateringRoomGuidDraft = storedRoomGuid || CATERING_ROOM_STANDARD;
    this.resetExpandedExtraComments();
  }

  private newExtraLineId(): string {
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.randomUUID === 'function'
    ) {
      return crypto.randomUUID();
    }
    return `line-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  private buildResolvedExtrasFromPayload(
    lines: Array<{
      line_id: string;
      extra_option_id: number;
      quantity: number;
      comment?: string;
    }>
  ): EventCateringExtraLineDTO[] {
    return lines.map((line) => {
      const opt = (this.response?.extra_options || []).find(
        (o) => o.id === line.extra_option_id
      );
      const comment = (line.comment ?? '').trim();
      return {
        line_id: line.line_id,
        extra_option_id: line.extra_option_id,
        quantity: line.quantity,
        label: opt?.label ?? `#${line.extra_option_id}`,
        is_active: opt?.is_active ?? true,
        comment: comment || null,
      };
    });
  }

  private enrichRoomsFromEventIfNeeded(eventGuid: string | null): void {
    if (!eventGuid || !this.response) {
      return;
    }
    const hasAdditionalRooms =
      (this.response.additional_rooms?.length ?? 0) > 0;
    const hasPrimaryRoom = !!this.response.predefined_room?.guid;
    if (hasAdditionalRooms && hasPrimaryRoom) {
      return;
    }
    this.http
      .get(`${environment.apiUrl}/events/${eventGuid}`, {
        responseType: 'text',
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rawBody) => {
          const rawEvent = this.parseApiPayload<any>(rawBody);
          const event = this.normalizeEventResponse(rawEvent);
          if (!event || !this.response) {
            return;
          }
          this.response.predefined_room = this.normalizePredefinedRoom(
            event.predefined_room
          );
          this.response.additional_rooms = this.normalizeAdditionalRooms(
            event.additional_rooms
          );
          this.cdr.detectChanges();
        },
      });
  }

  private isAutoIncludedExtraOption(
    extraOptionId: number | null | undefined,
    catalog: CateringExtraOptionDTO[]
  ): boolean {
    if (extraOptionId == null || extraOptionId <= 0) {
      return false;
    }
    const opt = catalog.find((o) => o.id === extraOptionId);
    return !!(opt?.is_active && opt?.auto_include);
  }

  private normalizeAbsoluteExtraLinesForCompare(
    rows: Array<{
      line_id?: string;
      extra_option_id?: number | null;
      quantity?: number | null;
      locked?: boolean;
      comment?: string | null;
    }>
  ): Array<{ lineId: string; id: number; q: number; c: string }> {
    const out: Array<{ lineId: string; id: number; q: number; c: string }> = [];
    (rows || []).forEach((r, index) => {
      const id = r.extra_option_id != null ? Number(r.extra_option_id) : 0;
      const q = r.quantity != null ? Number(r.quantity) : 0;
      const c = (r.comment ?? '').trim();
      if (id <= 0) {
        return;
      }
      if (!(q > 0 || r.locked || c)) {
        return;
      }
      out.push({
        lineId: r.line_id || `idx-${index}`,
        id,
        q,
        c,
      });
    });
    return out;
  }

  private normalizeDraftExtraLinesForCompare(
    rows: Array<{
      line_id?: string;
      extra_option_id?: number | null;
      quantity_adjustment?: number | null;
      locked?: boolean;
      comment?: string | null;
    }>
  ): Array<{ lineId: string; id: number; q: number; c: string }> {
    const out: Array<{ lineId: string; id: number; q: number; c: string }> = [];
    (rows || []).forEach((r, index) => {
      const id = r.extra_option_id != null ? Number(r.extra_option_id) : 0;
      const q = this.effectiveExtraQuantity(r);
      const c = (r.comment ?? '').trim();
      if (id <= 0) {
        return;
      }
      if (!(q > 0 || r.locked || c)) {
        return;
      }
      out.push({
        lineId: r.line_id || `idx-${index}`,
        id,
        q,
        c,
      });
    });
    return out;
  }

  private formatApiError(error: unknown, fallback: string): string {
    const httpErr = error as { error?: unknown; message?: string };
    const body = httpErr?.error;
    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body) as Record<string, unknown>;
        if (parsed && typeof parsed === 'object') {
          return this.formatApiError({ ...httpErr, error: parsed }, fallback);
        }
      } catch {
        return body.trim() || fallback;
      }
    }
    if (body && typeof body === 'object') {
      const o = body as { message?: string; error?: string; detail?: string };
      const tech = (o.detail || o.error || '').trim();
      const msg = (o.message || '').trim();
      if (tech && msg) return `${msg}: ${tech}`;
      return tech || msg || fallback;
    }
    return (httpErr?.message || '').trim() || fallback;
  }

  private escapeHtml(text: string): string {
    return (text || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  applyFilter(): void {
    this.dataSource.filter = JSON.stringify({
      search: this.searchTerm.trim().toLowerCase(),
      preference: this.selectedPreferenceFilter,
    });
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.selectedPreferenceFilter = '';
    this.applyFilter();
  }

  isFiltered(): boolean {
    return !!(this.searchTerm.trim() || this.selectedPreferenceFilter);
  }

  preferenceColorClass(pref: string): string {
    const classes = [
      'pref-chip-blue',
      'pref-chip-green',
      'pref-chip-orange',
      'pref-chip-purple',
      'pref-chip-teal',
    ];
    const hash = pref
      .split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return classes[hash % classes.length];
  }

  close(): void {
    this.closeRequested.emit();
  }

  getParticipantPreferences(item: ParticipantItem): string[] {
    return this.editedPreferences[this.participantKey(item)] || [];
  }

  onParticipantPreferencesChange(
    item: ParticipantItem,
    rawSelection: string[]
  ): void {
    this.editedPreferences[this.participantKey(item)] =
      this.normalizePreferences(rawSelection || []);
  }

  hasParticipantChanges(item: ParticipantItem): boolean {
    const current = this.normalizePreferences(item.catering_preferences || []);
    const edited = this.getParticipantPreferences(item);
    if (current.length !== edited.length) {
      return true;
    }
    return current.some((value, idx) => value !== edited[idx]);
  }

  isSavingParticipant(item: ParticipantItem): boolean {
    return this.savingParticipants.has(this.participantKey(item));
  }

  saveParticipantPreferences(item: ParticipantItem): void {
    const context = this.context;
    const participantUuid = item.user_uuid;
    if (!context?.eventGuid || !participantUuid) {
      return;
    }

    const key = this.participantKey(item);
    const preferences = this.getParticipantPreferences(item);
    this.savingParticipants.add(key);

    const payload = {
      participant: {
        user: {
          uuid: participantUuid,
        },
        role: item.role || 'attendee',
        status: item.status || 'pending',
        data: {
          catering_preferences: preferences,
        },
      },
    };

    this.http
      .put(
        `${environment.apiUrl}/events/${context.eventGuid}/participation`,
        payload
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          item.catering_preferences = [...preferences];
          this.rebuildSummaryFromCurrentItems();
          this.savingParticipants.delete(key);
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.errorMessage =
            error?.error?.message ||
            error?.message ||
            'Kunne ikke gemme forplejningsønsker.';
          this.savingParticipants.delete(key);
          this.cdr.detectChanges();
        },
      });
  }

  exportPdf(): void {
    const context = this.context;
    if (!context) {
      return;
    }

    const eventName = this.headerEventName;
    const dateRange = this.formatEventDateTimeRange(
      context.meta.startDate,
      context.meta.endDate
    );

    const items = this.response?.items || [];
    const cateringEligibleCount = this.cateringEligibleParticipantCount;
    const summary = this.mergedPreferenceSummary;
    const summaryKeys = this.sortSummaryComboKeys(Object.keys(summary));
    const now = new Date().toLocaleString('da-DK');

    const summaryRows = summaryKeys
      .map(
        (key) =>
          `<tr><td class="pref-name">${this.preferenceComboSummaryLabel(
            key
          )}</td><td class="pref-count">${summary[key]}</td></tr>`
      )
      .join('');

    const extraLines = this.eventExtraLinesDraft.filter(
      (row) => row.extra_option_id != null && row.extra_option_id > 0
    );
    const extrasRows = extraLines
      .map((row) => {
        const id = row.extra_option_id as number;
        const opt = (this.response?.extra_options || []).find(
          (o) => o.id === id
        );
        const label = this.escapeHtml(opt?.label || `#${id}`);
        const qty = this.effectiveExtraQuantity(row);
        const comment = (row.comment ?? '').trim();
        const commentHtml = comment
          ? `<div class="extra-comment">${this.escapeHtml(comment)}</div>`
          : '';
        return `<tr><td class="pref-name">${label}${commentHtml}</td><td class="pref-count">${qty}</td></tr>`;
      })
      .join('');

    const orderComment = this.cateringOrderCommentDraft.trim();
    const orderCommentHtml = orderComment
      ? `<h2>Kommentar til ordren</h2><p class="order-comment">${this.escapeHtml(
          orderComment
        )}</p>`
      : '';
    const cateringRoomHtml = `<p class="catering-room"><strong>Forplejning serveres i:</strong> ${this.escapeHtml(
      this.selectedCateringRoomLabel
    )}</p>`;

    const participantRows = items
      .map(
        (item) => `
      <tr>
        <td>${item.name || ''}</td>
        <td>${item.email || ''}</td>
        <td>${item.role || ''}</td>
        <td>${item.status || ''}</td>
        <td>${this.participantPrefsDisplayForPdf(
          item.catering_preferences
        )}</td>
      </tr>`
      )
      .join('');

    const html = `<!DOCTYPE html>
<html lang="da">
<head>
  <meta charset="UTF-8">
  <title>Forplejningsønsker – ${eventName}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; margin: 32px; color: #222; font-size: 13px; }
    h1 { font-size: 20px; color: #1565c0; margin: 0 0 4px; }
    .meta { color: #555; font-size: 12px; margin-bottom: 24px; line-height: 1.8; }
    h2 { font-size: 14px; margin: 24px 0 8px; border-bottom: 2px solid #1565c0; padding-bottom: 4px; color: #1565c0; text-transform: uppercase; letter-spacing: 0.5px; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #1565c0; color: #fff; padding: 7px 10px; text-align: left; font-size: 12px; }
    td { padding: 6px 10px; border-bottom: 1px solid #e0e0e0; vertical-align: top; }
    tr:nth-child(even) td { background: #f9f9f9; }
    .summary-table { max-width: 360px; }
    .summary-table td.pref-name { font-weight: 500; }
    .summary-table td.pref-count { text-align: right; font-weight: 700; color: #1565c0; width: 60px; }
    .extra-comment { margin-top: 4px; font-size: 11px; color: #666; font-weight: 400; }
    .order-comment { margin: 0 0 16px; padding: 10px 12px; background: #f5f5f5; border-left: 3px solid #1565c0; white-space: pre-wrap; }
    .catering-room { margin: 0 0 12px; color: #444; }
    .footer { margin-top: 28px; font-size: 11px; color: #999; border-top: 1px solid #eee; padding-top: 8px; }
    @media print {
      body { margin: 15px; }
      h2 { page-break-after: avoid; }
      tr { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <h1>Forplejningsønsker</h1>
  <div class="meta">
    <strong>${eventName}</strong><br>
    ${dateRange ? `Dato: ${dateRange}<br>` : ''}
    ${context.meta.location ? `Lokation: ${context.meta.location}<br>` : ''}
    Til forplejning: <strong>${cateringEligibleCount}</strong>
  </div>

  <h2>Opsummering</h2>
  <table class="summary-table">
    <thead><tr><th>Præference</th><th style="text-align:right;width:60px;">Antal</th></tr></thead>
    <tbody>${
      summaryRows ||
      '<tr><td colspan="2" style="color:#999;">Ingen registrerede ønsker</td></tr>'
    }</tbody>
  </table>

  <h2>Forplejningskatalog</h2>
  ${cateringRoomHtml}
  ${orderCommentHtml}
  <table class="summary-table">
    <thead><tr><th>Vare</th><th style="text-align:right;width:60px;">Ønsket antal</th></tr></thead>
    <tbody>${
      extrasRows ||
      '<tr><td colspan="2" style="color:#999;">Ingen varer registreret</td></tr>'
    }</tbody>
  </table>

  <h2>Deltagerliste (${items.length} registreringer, ${cateringEligibleCount} til forplejning)</h2>
  <table>
    <thead>
      <tr>
        <th>Navn</th>
        <th>Email</th>
        <th>Rolle</th>
        <th>Status</th>
        <th>Forplejningsønsker</th>
      </tr>
    </thead>
    <tbody>${
      participantRows ||
      '<tr><td colspan="5" style="color:#999;text-align:center;">Ingen deltagere</td></tr>'
    }</tbody>
  </table>

  <div class="footer">Genereret: ${now}</div>
</body>
</html>`;

    const printWindow = window.open('', '_blank', 'width=1100,height=750');
    if (!printWindow) {
      alert(
        'Popup-blokering forhindrede PDF-eksporten. Tillad popups for denne side og prøv igen.'
      );
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  }

  private mergeMeta(
    base: CateringWishesEventMeta,
    override: CateringWishesEventMeta | null
  ): CateringWishesEventMeta {
    if (!override) {
      return base;
    }
    return {
      eventName: override.eventName ?? base.eventName ?? null,
      guid: override.guid ?? base.guid ?? null,
      startDate: override.startDate ?? base.startDate ?? null,
      endDate: override.endDate ?? base.endDate ?? null,
      location: override.location ?? base.location ?? null,
    };
  }

  private extractGuidFromUrl(): string | null {
    const routerPath = (this.router.url || '').split('?')[0];
    const browserPath = (window.location.pathname || '').split('?')[0];
    const combined = `${routerPath}/${browserPath}`;
    return (
      combined.split('/').find((segment: string) => UUID_REGEX.test(segment)) ||
      null
    );
  }

  private participantKey(item: ParticipantItem): string {
    return item.user_uuid || `${item.user_id}`;
  }

  private initializeEditedPreferences(): void {
    this.editedPreferences = {};
    this.dataSource.data.forEach((item) => {
      this.editedPreferences[this.participantKey(item)] =
        this.normalizePreferences(item.catering_preferences || []);
    });
  }

  private normalizePreferences(values: string[]): string[] {
    return normalizeCateringPreferenceSelection(values);
  }

  private sortSummaryComboKeys(keys: string[]): string[] {
    return [...keys].sort((a, b) => {
      if (a === 'nothing') return -1;
      if (b === 'nothing') return 1;
      if (a === 'ingen_praeferencer') return -1;
      if (b === 'ingen_praeferencer') return 1;
      return this.preferenceComboSummaryLabel(a).localeCompare(
        this.preferenceComboSummaryLabel(b),
        'da'
      );
    });
  }

  private participantPrefsDisplayForPdf(
    prefs: string[] | null | undefined
  ): string {
    const normalized = this.normalizePreferences(prefs || []);
    if (normalized.length === 0) {
      return this.preferenceSummaryLabel('nothing');
    }
    return normalized.map((p) => this.preferenceSummaryLabel(p)).join(' & ');
  }

  private rebuildSummaryFromCurrentItems(): void {
    if (!this.response) {
      return;
    }
    const summary: Record<string, number> = {};
    this.dataSource.data.forEach((item) => {
      if (!this.shouldCountParticipantInSummary(item)) {
        return;
      }
      const comboKey = sharedPreferenceComboSummaryKey(
        item.catering_preferences
      );
      summary[comboKey] = (summary[comboKey] || 0) + 1;
    });
    const merged = mergeCateringPreferenceSummary(summary);
    const sortedKeys = this.sortSummaryComboKeys(Object.keys(merged));
    this.response.summary = sortedKeys.reduce((acc, key) => {
      acc[key] = merged[key];
      return acc;
    }, {} as Record<string, number>);
  }

  private shouldCountParticipantInSummary(item: ParticipantItem): boolean {
    return shouldCountParticipantForCatering(item.status);
  }

  formatEventDateTimeRange(
    start?: string | null,
    end?: string | null
  ): string {
    if (!start) {
      return '';
    }
    const startDate = new Date(start);
    if (Number.isNaN(startDate.getTime())) {
      return '';
    }
    const fmtDatePart = (d: Date) => {
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}.${mm}.${yyyy}`;
    };
    const fmtTime = (d: Date) => {
      const hh = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      return `${hh}:${min}`;
    };
    const fmtDateTime = (d: Date) => `${fmtDatePart(d)} ${fmtTime(d)}`;
    if (!end) {
      return fmtDateTime(startDate);
    }
    const endDate = new Date(end);
    if (Number.isNaN(endDate.getTime())) {
      return fmtDateTime(startDate);
    }
    const sameDay = startDate.toDateString() === endDate.toDateString();
    if (sameDay) {
      return `${fmtDatePart(startDate)} ${fmtTime(startDate)} – ${fmtTime(endDate)}`;
    }
    return `${fmtDateTime(startDate)} – ${fmtDateTime(endDate)}`;
  }

  private normalizeEventResponse(raw: any): any {
    if (!raw || typeof raw !== 'object') return null;
    if (raw.event_id) return raw;
    if (raw.data && typeof raw.data === 'object' && raw.data.event_id)
      return raw.data;
    if (raw.event && typeof raw.event === 'object' && raw.event.event_id)
      return raw.event;
    return raw;
  }

  /** Flade svar ud af typiske API-kuverter ({ data: {...} } osv.). */
  private unwrapCateringPreferencesPayload(
    v: unknown
  ): DunaCateringPreferencesResponse | null {
    if (this.isUsableCateringPreferencesResponse(v)) {
      return v;
    }
    if (!v || typeof v !== 'object' || Array.isArray(v)) {
      return null;
    }
    const o = v as Record<string, unknown>;
    for (const key of ['data', 'payload', 'result', 'body'] as const) {
      const inner = o[key];
      if (
        inner &&
        typeof inner === 'object' &&
        !Array.isArray(inner) &&
        this.isUsableCateringPreferencesResponse(inner)
      ) {
        return inner;
      }
    }
    return null;
  }

  private normalizeCateringPreferencesResponse(
    response: DunaCateringPreferencesResponse | null
  ): DunaCateringPreferencesResponse | null {
    if (!response) {
      return null;
    }
    return {
      ...response,
      predefined_room: this.normalizePredefinedRoom(response.predefined_room),
      additional_rooms: this.normalizeAdditionalRooms(
        response.additional_rooms
      ),
      catering_room_guid: response.catering_room_guid ?? null,
    };
  }

  private normalizeAdditionalRooms(raw: unknown): CateringRoomOptionDTO[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .map((room) => {
        if (!room || typeof room !== 'object') {
          return null;
        }
        const record = room as Record<string, unknown>;
        const guid = String(record['guid'] ?? '').trim();
        if (!guid) {
          return null;
        }
        const roomName = String(
          record['room_name'] ?? record['name'] ?? ''
        ).trim();
        return { guid, room_name: roomName || guid };
      })
      .filter((room): room is CateringRoomOptionDTO => room !== null);
  }

  private normalizePredefinedRoom(raw: unknown): CateringRoomOptionDTO | null {
    const rooms = this.normalizeAdditionalRooms(raw ? [raw] : []);
    return rooms[0] ?? null;
  }

  private unwrapCateringExtrasPatchResponse(v: unknown): {
    catering_extras?: EventCateringExtraLineDTO[];
    extras_summary?: EventCateringExtraLineDTO[];
    catering_order_comment?: string | null;
    catering_room_guid?: string | null;
    predefined_room?: CateringRoomOptionDTO | null;
    additional_rooms?: CateringRoomOptionDTO[];
  } | null {
    if (!v || typeof v !== 'object' || Array.isArray(v)) {
      return null;
    }
    const o = v as Record<string, unknown>;
    if (
      Array.isArray(o['catering_extras']) ||
      Array.isArray(o['extras_summary'])
    ) {
      return o as {
        catering_extras?: EventCateringExtraLineDTO[];
        extras_summary?: EventCateringExtraLineDTO[];
        catering_order_comment?: string | null;
        catering_room_guid?: string | null;
        additional_rooms?: CateringRoomOptionDTO[];
      };
    }
    for (const key of ['data', 'payload', 'result', 'body'] as const) {
      const inner = o[key];
      if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
        return this.unwrapCateringExtrasPatchResponse(inner);
      }
    }
    return null;
  }

  private isUsableCateringPreferencesResponse(
    v: unknown
  ): v is DunaCateringPreferencesResponse {
    if (!v || typeof v !== 'object' || Array.isArray(v)) {
      return false;
    }
    const o = v as Record<string, unknown>;
    const eid = o['event_id'];
    const hasEventId =
      (typeof eid === 'number' && Number.isFinite(eid)) ||
      (typeof eid === 'string' &&
        eid.trim() !== '' &&
        Number.isFinite(Number(eid)));
    if (!hasEventId) {
      return false;
    }
    if (!Array.isArray(o['items'])) {
      return false;
    }
    return true;
  }

  /**
   * Logger råt API-svar når catering/preferences ikke kan bruges (parse eller form).
   * Åbn browserkonsollen (F12) for at se detaljerne.
   */
  private logCateringPreferencesPayloadFailure(
    eventId: number,
    fallbackGuid: string | null,
    rawBody: unknown,
    parsed: unknown
  ): void {
    let lastJsonError: string | undefined;
    if (
      typeof rawBody === 'string' &&
      (parsed === null || parsed === undefined)
    ) {
      const trimmed = rawBody.trim();
      for (const candidate of this.buildJsonParseCandidates(trimmed)) {
        try {
          JSON.parse(candidate);
        } catch (e: unknown) {
          lastJsonError = e instanceof Error ? e.message : String(e);
        }
      }
    }
    const preview =
      typeof rawBody === 'string'
        ? rawBody.length > 12000
          ? `${rawBody.slice(0, 6000)}\n… [truncated, total ${
              rawBody.length
            } chars] …\n${rawBody.slice(-4000)}`
          : rawBody
        : rawBody;
    console.error(
      '[duna-catering-wishes] catering/preferences: ugyldigt eller uforventet svar',
      {
        url: `${environment.apiUrl}/events/catering/preferences/${eventId}`,
        eventId,
        fallbackGuid,
        typeofRawBody: typeof rawBody,
        rawBodyLength: typeof rawBody === 'string' ? rawBody.length : undefined,
        lastJsonParseError: lastJsonError,
        parsedPayload: parsed ?? null,
        rawBodyPreview: preview,
      }
    );
  }

  private buildJsonParseCandidates(trimmed: string): string[] {
    const candidates: string[] = [trimmed];
    const firstObjectStart = trimmed.indexOf('{');
    const lastObjectEnd = trimmed.lastIndexOf('}');
    if (firstObjectStart !== -1 && lastObjectEnd > firstObjectStart) {
      candidates.push(trimmed.slice(firstObjectStart, lastObjectEnd + 1));
    }
    const firstArrayStart = trimmed.indexOf('[');
    const lastArrayEnd = trimmed.lastIndexOf(']');
    if (firstArrayStart !== -1 && lastArrayEnd > firstArrayStart) {
      candidates.push(trimmed.slice(firstArrayStart, lastArrayEnd + 1));
    }
    const trailingJsonMatch = trimmed.match(/(\{[\s\S]*\}|\[[\s\S]*\])\s*$/);
    if (trailingJsonMatch?.[1]) {
      candidates.push(trailingJsonMatch[1]);
    }
    return Array.from(new Set(candidates));
  }

  private parseApiPayload<T>(rawBody: unknown): T | null {
    if (rawBody === null || rawBody === undefined) return null;

    if (typeof rawBody === 'object') {
      return rawBody as T;
    }

    if (typeof rawBody !== 'string') {
      return null;
    }

    const trimmed = rawBody.trim();
    if (!trimmed) return null;

    for (const candidate of this.buildJsonParseCandidates(trimmed)) {
      try {
        return JSON.parse(candidate) as T;
      } catch {
        // try next candidate
      }
    }

    return null;
  }

  private resolveEventName(event: any, fallbackGuid: string): string {
    const shortDescription = event?.description?.short_description;
    if (shortDescription && typeof shortDescription === 'object') {
      const values = Object.values(shortDescription) as Array<any>;
      const da = values.find((entry) =>
        (entry?.language_code || '').toLowerCase().startsWith('da')
      );
      const text = da?.translation_text || values[0]?.translation_text;
      if (typeof text === 'string' && text.trim()) return text.trim();
    }
    return event?.event_name || event?.guid || fallbackGuid;
  }

  private resolveEventLocation(event: any): string | null {
    return (
      event?.manual_location ||
      event?.predefined_location?.locations_name ||
      null
    );
  }

  private extractPreferencesFromParticipantData(data: any): string[] {
    if (!data || typeof data !== 'object') return [];
    const raw = data.catering_preferences ?? data.dietary_preferences;
    if (!raw) return [];
    if (Array.isArray(raw)) return this.normalizePreferences(raw as string[]);
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed))
          return this.normalizePreferences(parsed as string[]);
      } catch {
        return [];
      }
    }
    return [];
  }

  private configureFilterPredicate(): void {
    this.dataSource.filterPredicate = (
      item: ParticipantItem,
      filter: string
    ) => {
      const parsed = JSON.parse(filter);
      const matchesSearch =
        !parsed.search ||
        item.name?.toLowerCase().includes(parsed.search) ||
        (item.email?.toLowerCase() || '').includes(parsed.search);
      const matchesPref =
        !parsed.preference ||
        (item.catering_preferences || []).includes(parsed.preference);
      return matchesSearch && matchesPref;
    };
  }

  private refreshCateringGroups(eventGuid: string | null): void {
    if (!eventGuid) {
      this.groupFeatureAvailable = false;
      this.availableGroups = [];
      this.suppressGroupsAutoSave = true;
      this.selectedGroupIds = [];
      this.initialGroupIds = [];
      this.suppressGroupsAutoSave = false;
      return;
    }

    this.loadingGroups = true;
    this.suppressGroupsAutoSave = true;
    this.groupFeatureAvailable = true;
    this.groupErrorMessage = null;

    this.http
      .get<{ items: CateringGroupDTO[] }>(
        `${environment.apiUrl}/events/catering/groups`
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (availableResponse) => {
          this.availableGroups = availableResponse?.items || [];
          this.loadSelectedGroups(eventGuid);
        },
        error: () => {
          this.availableGroups = [];
          this.loadSelectedGroups(eventGuid);
        },
      });
  }

  private loadSelectedGroups(eventGuid: string): void {
    const url = this.cateringGroupsApiUrl();
    if (!url) {
      this.loadingGroups = false;
      this.suppressGroupsAutoSave = false;
      return;
    }

    this.http
      .get<{ items: CateringGroupDTO[] }>(url)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const selected = (response?.items || [])
            .map((item) => item?.catering_group_id || 0)
            .filter((value) => value > 0)
            .sort((a, b) => a - b);
          this.suppressGroupsAutoSave = true;
          this.initialGroupIds = selected;
          this.selectedGroupIds = [...selected];
          this.groupsSaveStatus = 'idle';
          this.loadingGroups = false;
          this.suppressGroupsAutoSave = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.loadingGroups = false;
          this.suppressGroupsAutoSave = false;
          if (error?.status === 401 || error?.status === 403) {
            this.groupFeatureAvailable = false;
          } else {
            this.groupErrorMessage = this.formatApiError(
              error,
              'Kunne ikke hente forplejningsgrupper.'
            );
          }
          this.cdr.detectChanges();
        },
      });
  }
}
